import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, Play, Eye, Tv, ChevronLeft, ChevronRight, Globe, Filter, X } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "@/App";
import { cn } from "@/lib/utils";

const DEFAULT_IMG = "https://static.prod-images.emergentagent.com/jobs/da293390-17b3-4a24-b107-f9f013e80fde/images/4db205155861085ef7e0eb5842a9f67cf7ec2f982a9bfd58790512d194599fcd.png";

function ChannelCard({ channel, idx }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.02, duration: 0.35 }}
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
    >
      <Link to={`/watch/${channel.id}`} data-testid={`live-channel-${channel.id}`}
        className="group block rounded-[18px] overflow-hidden border border-white/[0.06] bg-[#0F172A] hover:border-[#FFC928]/30 hover:shadow-[0_8px_30px_rgba(255,201,40,0.1)] transition-all duration-300">
        <div className="aspect-video relative bg-[#050814] overflow-hidden">
          <img src={channel.logo_url || DEFAULT_IMG} alt={channel.name || "Channel"}
            className="w-full h-full object-contain p-5 group-hover:scale-110 transition-transform duration-500"
            referrerPolicy="no-referrer" loading="lazy"
            onError={(e) => { e.target.src = DEFAULT_IMG; }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-transparent to-transparent opacity-60" />
          <div className="absolute top-2.5 left-2.5 flex items-center gap-1 px-2 py-0.5 rounded-lg bg-red-500/90 backdrop-blur-sm">
            <div className="w-1.5 h-1.5 rounded-full bg-white live-pulse" />
            <span className="text-[10px] font-bold text-white uppercase tracking-wider">Live</span>
          </div>
          <div className="absolute top-2.5 right-2.5 px-1.5 py-0.5 rounded bg-white/10 backdrop-blur-sm text-[10px] font-bold text-white/80">HD</div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 bg-[#FFC928] rounded-full flex items-center justify-center scale-0 group-hover:scale-100 transition-transform duration-300 shadow-[0_0_25px_rgba(255,201,40,0.4)]">
              <Play size={22} className="text-black fill-black ml-0.5" />
            </div>
          </div>
        </div>
        <div className="p-3.5">
          <h4 className="font-semibold text-sm truncate text-white mb-1 group-hover:text-[#FFC928] transition-colors">{channel.name}</h4>
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-[#94A3B8] uppercase tracking-wider font-medium">{channel.category_name || ""}</span>
            <div className="flex items-center gap-1 text-[10px] text-[#94A3B8]"><Eye size={10} /><span>{channel.views || 0}</span></div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default function LiveTV() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("fr") ? "fr" : "en";
  const [searchParams, setSearchParams] = useSearchParams();

  const [channels, setChannels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || null);
  const [selectedCountry, setSelectedCountry] = useState(searchParams.get("country") || null);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page") || "1", 10));
  const itemsPerPage = 24;

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/channels`).catch(() => ({ data: [] })),
      axios.get(`${API}/categories`).catch(() => ({ data: [] })),
      axios.get(`${API}/countries`).catch(() => ({ data: [] })),
    ]).then(([chRes, catRes, countryRes]) => {
      setChannels(Array.isArray(chRes.data) ? chRes.data : []);
      setCategories(Array.isArray(catRes.data) ? catRes.data : []);
      setCountries(Array.isArray(countryRes.data) ? countryRes.data : []);
      setLoading(false);
    });
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Sync filters to URL
  useEffect(() => {
    const params = {};
    if (search) params.search = search;
    if (selectedCategory) params.category = selectedCategory;
    if (selectedCountry) params.country = selectedCountry;
    if (currentPage > 1) params.page = String(currentPage);
    setSearchParams(params, { replace: true });
  }, [search, selectedCategory, selectedCountry, currentPage, setSearchParams]);

  const filteredChannels = channels.filter((ch) => {
    const matchesSearch = !search || (ch.name || "").toLowerCase().includes(search.toLowerCase());
    const matchesCategory = selectedCategory ? ch.category_id === selectedCategory : true;
    const matchesCountry = selectedCountry ? ch.country_id === selectedCountry : true;
    return matchesSearch && matchesCategory && matchesCountry;
  });

  const totalPages = Math.ceil(filteredChannels.length / itemsPerPage);
  const paginatedChannels = filteredChannels.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const hasFilters = search || selectedCategory || selectedCountry;

  const clearFilters = () => {
    setSearch("");
    setSelectedCategory(null);
    setSelectedCountry(null);
    setCurrentPage(1);
  };

  const catName = (cat) => locale === "fr" ? (cat.name_fr || cat.name) : (cat.name_en || cat.name);

  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8" data-testid="livetv-loading">
        <div className="h-10 w-64 bg-[#0F172A] rounded-xl animate-pulse mb-6" />
        <div className="flex gap-3 mb-6">
          <div className="h-11 flex-grow bg-[#0F172A] rounded-2xl animate-pulse" />
          <div className="h-11 w-36 bg-[#0F172A] rounded-xl animate-pulse" />
          <div className="h-11 w-36 bg-[#0F172A] rounded-xl animate-pulse" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="rounded-[18px] overflow-hidden bg-[#0F172A] border border-white/[0.06]">
              <div className="aspect-video bg-[#172033] animate-pulse" />
              <div className="p-3.5"><div className="h-4 w-3/4 bg-[#172033] rounded animate-pulse mb-2" /><div className="h-3 w-1/2 bg-[#172033] rounded animate-pulse" /></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6" data-testid="livetv-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-white flex items-center gap-3" style={{ fontFamily: 'Poppins' }} data-testid="livetv-title">
          <Tv size={28} className="text-[#FFC928]" />
          {locale === "fr" ? "TV en direct" : "Live TV"}
        </h1>
        <p className="text-[#94A3B8] text-sm mt-1">
          {locale === "fr"
            ? `${filteredChannels.length} chaîne${filteredChannels.length !== 1 ? "s" : ""} disponible${filteredChannels.length !== 1 ? "s" : ""}`
            : `${filteredChannels.length} channel${filteredChannels.length !== 1 ? "s" : ""} available`}
        </p>
      </div>

      {/* Search & Filters */}
      <section className="mb-6" data-testid="livetv-filters">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-grow">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={18} />
            <input
              type="text"
              placeholder={t("home.searchChannels")}
              data-testid="livetv-search"
              className="w-full pl-11 pr-4 py-3 rounded-2xl bg-white/[0.05] border border-white/[0.08] text-white placeholder:text-[#94A3B8]/60 focus:outline-none focus:ring-2 focus:ring-[#FFC928]/20 text-sm"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <select
            data-testid="livetv-category-filter"
            className="px-4 py-3 rounded-xl bg-white/[0.05] border border-white/[0.08] text-white focus:outline-none text-sm appearance-none cursor-pointer min-w-[160px]"
            value={selectedCategory || ""}
            onChange={(e) => { setSelectedCategory(e.target.value || null); setCurrentPage(1); }}
          >
            <option value="">{t("home.allCategories")}</option>
            {categories.map(c => <option key={c.id} value={c.id}>{catName(c)}</option>)}
          </select>
          <select
            data-testid="livetv-country-filter"
            className="px-4 py-3 rounded-xl bg-white/[0.05] border border-white/[0.08] text-white focus:outline-none text-sm appearance-none cursor-pointer min-w-[160px]"
            value={selectedCountry || ""}
            onChange={(e) => { setSelectedCountry(e.target.value || null); setCurrentPage(1); }}
          >
            <option value="">{t("home.allCountries")}</option>
            {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        {hasFilters && (
          <button onClick={clearFilters} className="mt-3 text-xs text-[#FFC928] hover:underline flex items-center gap-1" data-testid="livetv-clear-filters">
            <X size={12} /> {t("home.clearFilters")}
          </button>
        )}
      </section>

      {/* Channel Grid */}
      <section>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4" data-testid="livetv-grid">
          {paginatedChannels.map((ch, idx) => <ChannelCard key={ch.id} channel={ch} idx={idx} />)}
        </div>

        {filteredChannels.length === 0 && (
          <div className="text-center py-24 rounded-[24px] bg-[#0F172A] border border-dashed border-white/[0.08]" data-testid="livetv-no-results">
            <Tv className="mx-auto text-[#94A3B8]/30 mb-4" size={48} />
            <h3 className="text-lg font-medium text-white">{t("home.noChannelsTitle")}</h3>
            <p className="text-[#94A3B8] text-sm mt-1">{t("home.noChannelsSubtitle")}</p>
            {hasFilters && (
              <button onClick={clearFilters} className="mt-4 px-5 py-2.5 bg-[#FFC928] text-black rounded-xl font-bold text-sm hover:shadow-[0_0_20px_rgba(255,201,40,0.3)] transition-all">
                {t("home.clearFilters")}
              </button>
            )}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-8 flex justify-center" data-testid="livetv-pagination">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 rounded-xl bg-white/[0.05] border border-white/[0.08] text-[#94A3B8] hover:text-white disabled:opacity-30 transition-colors text-sm"
                data-testid="livetv-prev"
              >
                {t("home.previous")}
              </button>

              {(() => {
                const pages = [];
                const maxVisible = 5;
                let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                let end = Math.min(totalPages, start + maxVisible - 1);
                if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);

                for (let p = start; p <= end; p++) {
                  pages.push(
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      className={cn(
                        "w-10 h-10 rounded-xl flex items-center justify-center text-sm font-medium transition-all",
                        currentPage === p
                          ? "bg-[#FFC928] text-black shadow-[0_0_15px_rgba(255,201,40,0.2)]"
                          : "bg-white/[0.05] border border-white/[0.08] text-[#94A3B8] hover:text-white"
                      )}
                      data-testid={`livetv-page-${p}`}
                    >
                      {p}
                    </button>
                  );
                }
                return pages;
              })()}

              <button
                onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 rounded-xl bg-white/[0.05] border border-white/[0.08] text-[#94A3B8] hover:text-white disabled:opacity-30 transition-colors text-sm"
                data-testid="livetv-next"
              >
                {t("home.next")}
              </button>
            </div>
          </div>
        )}

        {/* Page indicator */}
        {totalPages > 1 && (
          <p className="text-center text-xs text-[#94A3B8]/60 mt-3" data-testid="livetv-page-indicator">
            {locale === "fr" ? `Page ${currentPage} sur ${totalPages}` : `Page ${currentPage} of ${totalPages}`}
          </p>
        )}
      </section>
    </div>
  );
}
